import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { z } from "zod";
import { env } from "../config/env";
import { openExternalUrl } from "../tools/open-external-url";

// Zod schemas for postMessage event validation
const FigmaMessageSchema = z.object({
  type: z.string(),
});

const NavigateToUrlEventDetailSchema = z.object({
  url: z.string(),
});

// See: https://www.figma.com/developers/embed#query-parameters-prototypes
interface FigmaProtoEmbedQueryParams {
  "embed-host": string;
  "client-id"?: string;
  "node-id": string;
  "starting-point-node-id"?: string;
  footer?: "true" | "false";
  "viewport-controls"?: "true" | "false";
  "disable-default-keyboard-nav"?: "true" | "false";
  "show-proto-sidebar"?: "true" | "false";
  "hotspot-hints"?: "true" | "false";
  "device-frame"?: "true" | "false";
  scaling?:
    | "scale-down"
    | "contain"
    | "min-zoom"
    | "scale-down-width"
    | "fit-width"
    | "free";
  "content-scaling"?: "fixed" | "responsive";
  // Old Figma embed params, still supported
  "hide-ui": "1" | "0";
}

const defaultQueryParams: Partial<FigmaProtoEmbedQueryParams> = {
  "embed-host": "app.juisci.com",
  "device-frame": "false",
  "hotspot-hints": "false",
  "show-proto-sidebar": "false",
  footer: "false",
  "content-scaling": "fixed",
  scaling: "scale-down-width",
  "viewport-controls": "true",
  "disable-default-keyboard-nav": "true",
  "hide-ui": "1",
};

const figmaClientId = env.figmaClientId === null ? "" : env.figmaClientId;

const getFigmaEmbedUrl = (
  url: string,
  queryParams?: Partial<FigmaProtoEmbedQueryParams>
): string | null => {
  let urlObj: URL;
  try {
    urlObj = new URL(url);
  } catch {
    return null;
  }
  // Allow figma site urls in subdomain.figma.site
  // TODO: use custom domain instead of figma.site
  if (/^[^.]+\.figma\.site$/.test(urlObj.hostname)) {
    return url;
  }
  // Our own generated HTML, published through `/jup --public`. Passed through
  // untouched for the same reason figma.site is: it is already the page to
  // show, so the Figma query params and the `embed.figma.com` rewrite below
  // would turn a working url into a broken one.
  //
  // EXACT host. A suffix test would also accept `juisci.xyz.example.com`.
  if (urlObj.hostname === "juisci.xyz") {
    return url;
  }
  for (const [key, value] of Object.entries({
    ...defaultQueryParams,
    ...queryParams,
    "client-id": figmaClientId,
  })) {
    urlObj.searchParams.set(key, value);
  }
  // make sure the hostname is embed.figma.com and not www.figma.com
  urlObj.hostname = "embed.figma.com";
  return urlObj.toString();
};

interface FigmaEmbedProps {
  url: string;
  title?: string;
  queryParams?: Partial<FigmaProtoEmbedQueryParams>;
  onMessage?: (event: MessageEvent) => void;
  onLoad?: () => void;
}

export default function FigmaEmbed({
  url,
  title,
  queryParams,
  onMessage,
  onLoad,
}: FigmaEmbedProps) {
  const figmaEmbedUrl = getFigmaEmbedUrl(url, queryParams);
  const isFigmaEmbed = figmaEmbedUrl?.startsWith("https://embed.figma.com/");
  const history = useHistory();

  useEffect(() => {
    if (!isFigmaEmbed) {
      return;
    }
    const handleMessage = (event: MessageEvent) => {
      console.log("handleMessage", event);
      const parsed = FigmaMessageSchema.safeParse(event.data);
      if (parsed.success && parsed.data.type === "INITIAL_LOAD") {
        onLoad?.();
      }
    };

    const handleNavigateToUrl = (event: Event) => {
      const eventDetail = NavigateToUrlEventDetailSchema.safeParse(
        (event as CustomEvent).detail
      );
      if (!eventDetail.success) {
        return;
      }
      const eventUrl = eventDetail.data.url;
      if (!eventUrl.includes("www.figma.com/exit")) {
        return;
      }
      const figmaUrl = new URL(eventUrl);
      const urlParam = figmaUrl.searchParams.get("url");
      if (urlParam === null) {
        return;
      }
      const url = new URL(urlParam);
      if (
        !url.host.includes(".juisci.com") &&
        !url.host.includes("localhost")
      ) {
        openExternalUrl(url.toString());
        return;
      }
      const newPath = url.pathname + url.search;
      history.push(newPath);
    };

    globalThis.addEventListener("navigateToUrl", handleNavigateToUrl);
    window.addEventListener("message", handleMessage);

    return () => {
      globalThis.removeEventListener("navigateToUrl", handleNavigateToUrl);
      window.removeEventListener("message", handleMessage);
    };
  }, [isFigmaEmbed, onMessage]);

  if (!figmaEmbedUrl) {
    return null;
  }

  return (
    <iframe
      title={title || "Figma Embed"}
      width="100%"
      height="100%"
      style={{ backgroundColor: "#fff", border: 0 }}
      src={figmaEmbedUrl}
      onLoad={onLoad}
    />
  );
}
